/**
 * Pure Redux/path selectors for partner and account IDs.
 * Kept separate from usePartnerData to avoid circular imports with action modules.
 */
import { CloudTypeEnum as CloudType } from '../../types'
import type { ProductType } from '../../types'

/** Matches PartnerType enum values without importing partner/action (avoids circular deps). */
export type PartnerTypeValue = 'user' | 'admin'

/** Redux stores product keys uppercased (OPPSYNC, FLYOUT, CLOUDFAAS). */
type SelectedProductKey = Uppercase<ProductType>
type CloudAccountsByCloud = Partial<Record<CloudType, string>>
type SelectedAccountIdsByProduct = Partial<
  Record<SelectedProductKey, CloudAccountsByCloud>
>

const getFirstCloudAccountId = (
  cloudAccounts: CloudAccountsByCloud
): string | undefined => {
  for (const accountId of Object.values(cloudAccounts)) {
    if (typeof accountId === 'string') {
      return accountId
    }
  }
  return undefined
}

const ADMIN_PARTNER_ID_FROM_URL_PATTERN =
  /\/admin\/partners\/(?!create\b)([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i

/**
 * Extracts the partner ID from an admin partner detail URL (e.g. /oppsync/admin/partners/:id/...).
 */
export const getAdminPartnerIdFromPathname = (
  pathname: string
): string | undefined => {
  const match = pathname.match(ADMIN_PARTNER_ID_FROM_URL_PATTERN)
  return match?.[1]
}

/**
 * Pure function to get tenant partner ID from state
 * Can be used in both hooks and Redux thunks
 */
export const getTenantPartnerIdFromState = (
  state: any,
  userType: PartnerTypeValue = 'user',
  pathname: string = ''
): string | undefined => {
  if (userType === 'admin') {
    const selectedPartnerId =
      state.AccountData?.admin?.selectedPartnerId?.trim()
    if (selectedPartnerId) {
      return selectedPartnerId
    }

    const partnerIdFromUrl = getAdminPartnerIdFromPathname(pathname)
    if (partnerIdFromUrl) {
      return partnerIdFromUrl
    }

    return state.AccountData?.admin?.partnerData?.partnerId
  }

  const tenantId = state.tenants?.selectedTenantId
  const partnerDataId = state.AccountData?.[userType]?.partnerData?.partnerId

  return tenantId || partnerDataId
}

const getProductFromUrl = (pathname: string): ProductType | undefined => {
  const lowerPath = pathname.toLowerCase()

  if (
    lowerPath.includes('/oppsync') ||
    lowerPath.includes('/cosell') ||
    lowerPath.includes('/co-sell')
  )
    return 'oppsync'
  if (lowerPath.includes('/flyout') || lowerPath.includes('/marketplace'))
    return 'flyout'
  if (lowerPath.includes('/cloudfaas')) return 'cloudfaas'

  return undefined
}

const getCloudFromUrl = (pathname: string): CloudType | undefined => {
  const lowerPath = pathname.toLowerCase()

  if (lowerPath.includes('/cloud/aws') || lowerPath.includes('/aws'))
    return CloudType.AWS
  if (lowerPath.includes('/cloud/azure') || lowerPath.includes('/azure'))
    return CloudType.AZURE
  if (lowerPath.includes('/cloud/gcp') || lowerPath.includes('/gcp'))
    return CloudType.GCP
  if (lowerPath.includes('/cloud/redhat') || lowerPath.includes('/redhat'))
    return CloudType.REDHAT

  return undefined
}

/**
 * Labra cloud account UUID from HubSpot embedded routes.
 */
export const getHubspotCosellAccountIdFromPath = (pathname: string): string => {
  const cosellMatch = pathname.match(
    /\/cosell\/(?:aws|azure|gcp)\/([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})(?:\/|$)/i
  )
  if (cosellMatch?.[1]) return cosellMatch[1]

  const portalCloudMatch = pathname.match(
    /\/portal\/[^/]+\/(?:aws|azure|gcp)\/([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})\/referral/i
  )
  return portalCloudMatch?.[1] ?? ''
}

/**
 * Core logic to get partner ID from state (pure function)
 */
export const getPartnerIdFromState = (
  state: any,
  pathname: string = '',
  userType: PartnerTypeValue = 'user'
): string | undefined => {
  const productFromUrl = getProductFromUrl(pathname)
  const cloudFromUrl = getCloudFromUrl(pathname)

  const selectedAccountIds = state.accountSelection?.selectedAccountIds
  const tenantPartnerId = getTenantPartnerIdFromState(state, userType, pathname)

  if (!productFromUrl) {
    const oppsyncAwsId = (
      selectedAccountIds as
        | Record<string, Partial<Record<CloudType, string>>>
        | undefined
    )?.OPPSYNC?.[CloudType.AWS]
    if (oppsyncAwsId) {
      return oppsyncAwsId
    }
  }

  if (productFromUrl) {
    const productKey = productFromUrl.toUpperCase() as SelectedProductKey
    const cloudAccounts = (
      selectedAccountIds as SelectedAccountIdsByProduct | undefined
    )?.[productKey]

    if (cloudAccounts) {
      if (cloudFromUrl && cloudAccounts[cloudFromUrl]) {
        return cloudAccounts[cloudFromUrl]
      }

      const firstCloudAccountId = getFirstCloudAccountId(cloudAccounts)
      return firstCloudAccountId || tenantPartnerId
    }
  }

  return tenantPartnerId
}

export const getOppsyncAwsAccountIdFromState = (
  state: any
): string | undefined => {
  const accounts = state.accountSelection?.availableAccounts
  const availableAccounts = Array.isArray(accounts) ? accounts : []
  const oppsyncAwsAccount = availableAccounts.find(
    (account: any) =>
      account?.product?.toUpperCase() === 'OPPSYNC' && account?.cloud === 'AWS'
  )
  return oppsyncAwsAccount?.id
}

export const getOppsyncAzureAccountIdFromState = (
  state: any
): string | undefined => {
  const accounts = state.accountSelection?.availableAccounts
  const availableAccounts = Array.isArray(accounts) ? accounts : []
  const oppsyncAzureAccount = availableAccounts.find(
    (account: any) =>
      account?.product?.toUpperCase() === 'OPPSYNC' &&
      account?.cloud === 'AZURE'
  )
  return oppsyncAzureAccount?.id
}

export const getHubspotOppsyncAccountIdFromState = (state: any): string =>
  getOppsyncAwsAccountIdFromState(state) ||
  getOppsyncAzureAccountIdFromState(state) ||
  ''
